import { iPermissionFields, PermissionLevelEnum, PermissionSection } from "@models/PermissionSection";
import { components } from "@apiSchema";
import { getFormattedThousand } from "@helpers";
import { ID } from "@models/index";

export const DEFAULT_VALUE_PLACEHOLDER = undefined;

type ApiModel = components["schemas"]["InstantChartDetailsModel"];

interface iData {
    apiModel?: ApiModel;
}

export interface iInstantChartWord {
    title: string;
    penetration?: number;
    oneYearTrend?: number;
    fourYearTrend?: number;
    knowIt?: number;
    haveTriedIt?: number;
    loveOrLikeIt?: number;
}

interface iInstantChartData {
    id: ID;
    title: string;
    description: string;
    baseSizeDescription: string;
    words: iInstantChartWord[];
}

export interface iInstantChart extends iPermissionFields {
    data: iInstantChartData;
}

export default class InstantChart implements iInstantChart {
    static defaultData: iInstantChartData = {
        id: 0,
        title: '',
        description: '',
        baseSizeDescription: '',
        words: [],
    };
    static fakeData: iInstantChartData = {
        id: 0,
        title: 'Fake Instant Chart',
        description: 'fake description',
        baseSizeDescription: 'fake description',
        words: InstantChart.generateFakeWords(8),
    };

    permissionLevel = PermissionLevelEnum.None;
    hasData = true;
    data = InstantChart.defaultData;

    constructor(data?: iData) {
        if (data && data.apiModel) {
            this.mapFromApi(data.apiModel);
        }
    }

    private setData (model: iInstantChart) {
        ({
            data: this.data,
            hasData: this.hasData,
            permissionLevel: this.permissionLevel,
        } = model);
    }

    private mapFromApi (apiModel: ApiModel) {
        const {
            id,
            flavorPermissionLevel: API_flavorPermissionLevel,
            description,
            baseSizes,
            title,
            wordData,
        } = apiModel;
        const {
            incidence: baseSizeIncidence,
            penetration: baseSizePenetration,
        } = baseSizes ?? {};
        const {
            data: wordsData = [],
            permissionLevel: API_mtPermissionLevel,
        } = wordData ?? {};
        let data: iInstantChartData = InstantChart.fakeData;
        let permissionLevel = PermissionLevelEnum.None;
        let flavorPermissionLevel = PermissionSection.getPermissionLevel(API_flavorPermissionLevel);
        let mtPermissionLevel = PermissionSection.getPermissionLevel(API_mtPermissionLevel);

        if (mtPermissionLevel !== PermissionLevelEnum.None) {
            if (!flavorPermissionLevel) {
                permissionLevel = PermissionLevelEnum.Limited;
            } else {
                permissionLevel = PermissionLevelEnum.Full;
            }

            data = {
                id: id ?? InstantChart.defaultData.id,
                title: title ?? InstantChart.defaultData.title,
                description: description ?? InstantChart.defaultData.description,
                baseSizeDescription: `The figures are based on analysis of ${getFormattedThousand(baseSizeIncidence ?? 0)} items on ${getFormattedThousand(baseSizePenetration ?? 0)} distinct restaurant menus nationwide.`,
                words: (wordsData ?? []).map(word => ({
                    title: word.name ?? '',
                    penetration: word.penetrations?.currentYear ?? undefined,
                    oneYearTrend: word.penetrations?.oneYearTrend ?? undefined,
                    fourYearTrend: word.penetrations?.fourYearTrend ?? undefined,
                    knowIt: word.flavorData?.knowIt ?? undefined,
                    haveTriedIt: word.flavorData?.haveTriedIt ?? undefined,
                    loveOrLikeIt: word.flavorData?.loveOrLikeIt ?? undefined,
                }))
            }
        } else {
            data.title = apiModel?.title ?? InstantChart.defaultData.title;
        }

        this.setData({
            permissionLevel,
            hasData: true,
            data,
        });
    }

    static generateFakeWords(count: number = 1): iInstantChartWord[] {
        return Array(count).fill(null).map((_, index) => ({
            title: `Keyword ${index + 1}`,
            penetration: DEFAULT_VALUE_PLACEHOLDER,
            oneYearTrend: DEFAULT_VALUE_PLACEHOLDER,
            fourYearTrend: DEFAULT_VALUE_PLACEHOLDER,
            knowIt: DEFAULT_VALUE_PLACEHOLDER,
            haveTriedIt: DEFAULT_VALUE_PLACEHOLDER,
            loveOrLikeIt: DEFAULT_VALUE_PLACEHOLDER,
        }));
    }
}